import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent } from '@/components/ui';
import { Button, Input, Label, Spinner } from '@/components/ui';
import internetqLogo from '@/assets/internetq-logo.png';

/**
 * Challenge-State in SessionStorage fuer NEW_PASSWORD_REQUIRED.
 * TTL: 5 Minuten. Wird in change-password.tsx gelesen.
 */
const CHALLENGE_STATE_KEY = 'auth_challenge_state';

function saveChallengeState(email: string) {
  sessionStorage.setItem(
    CHALLENGE_STATE_KEY,
    JSON.stringify({ email, timestamp: Date.now() })
  );
}

/**
 * Mapt Cognito-Fehler auf deutsche UI-Meldungen.
 * Spec 0043 Section 4.1: Kein User-Existence-Leaking.
 */
function getLoginErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const name = error.name;

    // Gleiche Meldung fuer falsche Credentials UND unbekannten User
    if (name === 'NotAuthorizedException' || name === 'UserNotFoundException') {
      return 'E-Mail oder Passwort ist falsch.';
    }
    if (name === 'UserNotConfirmedException') {
      return 'Ihr Konto ist noch nicht bestätigt. Kontaktieren Sie den Administrator.';
    }
    if (name === 'TooManyRequestsException' || name === 'LimitExceededException') {
      return 'Zu viele Anmeldeversuche. Bitte warten Sie einen Moment.';
    }
    if (name === 'UserLambdaValidationException') {
      return 'Anmeldung fehlgeschlagen. Kontaktieren Sie den Administrator.';
    }
    // Disabled-User Fehler — gleiche generische Meldung
    if (name === 'UserDisabledException') {
      return 'E-Mail oder Passwort ist falsch.';
    }

    // Netzwerkfehler
    if (error.message?.includes('fetch') || error.message?.includes('network') || error.message?.includes('Network')) {
      return 'Verbindung zum Server fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung.';
    }
  }

  return 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.';
}

/**
 * Login-Seite mit Email/Passwort (SRP Direct Auth).
 * Oeffentliche Route, kein AuthGuard.
 *
 * @see Spec 0043 Section 4.1
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, isAuthenticated, isLoading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hinweis-Flags aus Query-Parametern
  const resetSuccess = searchParams.get('reset') === 'success';
  const challengeExpired = searchParams.get('expired') === 'challenge';

  // Bereits eingeloggt → weiter zum Dashboard
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (isAuthenticated && !authLoading) {
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Bitte geben Sie E-Mail und Passwort ein.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signIn(email.trim(), password);

      if (result.success) {
        const redirectTarget = sessionStorage.getItem('redirectAfterLogin') || '/';
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectTarget, { replace: true });
        return;
      }

      if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
        saveChallengeState(email.trim());
        navigate('/change-password', { replace: true });
        return;
      }

      // Unbekannte Challenge
      setError('Anmeldung fehlgeschlagen. Kontaktieren Sie den Administrator.');
    } catch (err) {
      // PasswordResetRequiredException → Weiterleitung zu Forgot-Password
      if (err instanceof Error && err.name === 'PasswordResetRequiredException') {
        navigate('/forgot-password', { replace: true });
        return;
      }
      setError(getLoginErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-8 pb-6">
          {/* Brand */}
          <div className="mb-6 flex flex-col items-center gap-2">
            <img
              src={internetqLogo}
              alt="InternetQ"
              className="h-10 w-auto dark:brightness-0 dark:invert"
            />
            <span className="text-xs font-medium text-muted-foreground">Admin Panel</span>
          </div>

          {/* Erfolgs-Meldung nach Passwort-Reset */}
          {resetSuccess && (
            <div className="mb-4 rounded-md bg-green-50 p-3 text-center text-sm text-green-700 dark:bg-green-950 dark:text-green-300">
              Passwort wurde erfolgreich geändert. Bitte melden Sie sich an.
            </div>
          )}

          {/* Hinweis bei abgelaufenem Challenge-State */}
          {challengeExpired && (
            <div className="mb-4 rounded-md bg-amber-50 p-3 text-center text-sm text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              Ihre Sitzung ist abgelaufen. Bitte erneut anmelden.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Fehler-Anzeige (inline) */}
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-center text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? 'Verbergen' : 'Anzeigen'}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  Anmelden…
                </span>
              ) : (
                'Anmelden'
              )}
            </Button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              >
                Passwort vergessen?
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
